import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Breadcrumb from '../components/Breadcrumb';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { getGrandhamData } from '../utils/grandhamDataUtils';
import { krishna_yajurvedam_tree_labels } from '../data/grandhamData';

export default function PathasalaExamStatus() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [vatuList, setVatuList] = useState([]);

  const labels = {
    telugu: {
      title: 'పాఠశాల పరీక్ష నమోదు స్థితి',
      loading: 'లోడవుతోంది...',
      noData: 'వటువులు కనుగొనబడలేదు',
      noGrandham: 'డేటా లేదు',
      notCompleted: 'నమోదు పూర్తి కాలేదు'
    },
    sanskrit: {
      title: 'पाठशाला परीक्षा पञ्जीकरण स्थितिः',
      loading: 'आरोप्यते...',
      noData: 'वाटुवुः न प्राप्ताः',
      noGrandham: 'दत्तांशः नास्ति',
      notCompleted: 'पञ्जीकरण अपूर्णम्'
    }
  };

  // Fetch vatus for the pathasala
  useEffect(() => {
    const fetchVatus = async () => {
      if (!user || !user.pathasala_id) return;

      setLoading(true);
      try {
        const res = await api.get(`/api/vatu/by-pathasala/${user.pathasala_id}`);
        const data = await res.json();

        if (res.ok && data.success && Array.isArray(data.data)) {
          setVatuList(data.data);
        }
      } catch (err) {
        console.error('Error fetching vatus:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchVatus();
  }, [user]);

  // Get the last (most recent) grandham name for display
  const getLastGrandhamName = (vatu) => {
    const data = getGrandhamData(vatu);
    const selectedTypes = Object.keys(data);
    if (selectedTypes.length === 0) {
      return labels[lang].noGrandham;
    }
    const phase = data.krishna_yajurveda?.current || data.krishna_yajurveda?.previous || {};
    const years = Object.keys(phase).filter((key) => /^\d{4}$/.test(key));
    const selected = years.length > 0
      ? phase[years.sort()[years.length - 1]]
      : phase;
    const labelsForLanguage = krishna_yajurvedam_tree_labels;
    const names = Object.keys(selected)
      .filter((key) => key !== 'no_data' && key !== '_selected')
      .map((key) => labelsForLanguage[key]?.[lang] || labelsForLanguage[key]?.english || key);
    return names.length > 0 ? names.join(', ') : labels[lang].noGrandham;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Breadcrumb
        items={[
          { label: lang === 'telugu' ? 'పరీక్ష' : 'परीक्षा', path: '/parikshaMgmt' },
          { label: lang === 'telugu' ? 'పాఠశాల పరీక్ష స్థితి' : 'पाठशाला परीक्षा स्थिति', path: '/pathasala/status' }
        ]}
      />
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          backgroundColor: '#fff'
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            mb: { xs: 3, md: 4 },
            color: '#7f4b2d',
            fontWeight: 600,
            textAlign: 'center',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2rem' },
            borderBottom: '3px solid #7f4b2d',
            pb: 2
          }}
        >
          {labels[lang].title}
        </Typography>

        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mt: 4,
              gap: 2
            }}
          >
            <CircularProgress size={30} sx={{ color: '#7f4b2d' }} />
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              {labels[lang].loading}
            </Typography>
          </Box>
        )}

        {!loading && vatuList.length === 0 && (
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              py: 4
            }}
          >
            {labels[lang].noData}
          </Typography>
        )}

        {!loading && vatuList.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 4,
            justifyContent: 'flex-start',
            alignItems: 'flex-start'
          }}>
            {vatuList.map((vatu) => (
              <Box
                key={vatu._id}
                onClick={() => navigate(`/vatuvu?vatu_id=${vatu.vatu_id}`)}
                sx={{
                  width: { xs: 'calc(50% - 16px)', sm: 'calc(33.333% - 22px)', md: 'calc(25% - 24px)', lg: 'calc(20% - 26px)' },
                  minWidth: '180px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              >
                {/* Circular Photo */}
                <Box
                  sx={{
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '4px solid #7f4b2d',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    mb: 2,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                      borderColor: '#4caf50'
                    }
                  }}
                >
                  <img
                    src={vatu.photo_download_link || '/assets/default-avatar.png'}
                    alt={vatu.vatu_name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center top'
                    }}
                  />
                  
                  {/* Red overlay if registration not completed */}
                  {!vatu.completed && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(211, 47, 47, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          textAlign: 'center',
                          px: 1,
                          lineHeight: 1.2
                        }}
                      >
                        {labels[lang].notCompleted}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Name */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#7f4b2d',
                    mb: 0.5,
                    textAlign: 'center',
                    fontSize: '0.95rem',
                    lineHeight: 1.3,
                    minHeight: '2.6em',
                    display: 'flex',
                    alignItems: 'center',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {vatu.vatu_name}
                </Typography>

                {/* Single Grandham Label */}
                <Typography
                  variant="caption"
                  sx={{
                    color: '#4caf50',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    textAlign: 'center',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {getLastGrandhamName(vatu)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
}
